import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import i18n from "@/i18n";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      // Prefer server-stored language once available so it follows the user
      // across devices. Falls back silently if unset.
      const lang = res.data?.language;
      if (lang && ["en", "es"].includes(lang) && i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
    } catch (e) {
      logger.error("auth/me failed", e);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip /me check
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      logger.error("logout failed", e);
    }
    // Clear any legacy client-side session data. `session_token` is no
    // longer stored in localStorage, but keep the cleanup as a one-time
    // migration for users still carrying it from an older build.
    try {
      localStorage.removeItem("session_token");
      sessionStorage.clear();
    } catch (storageErr) {
      // Storage APIs can throw in private-mode Safari — non-fatal here since
      // the auth cookie has already been invalidated by the server call above.
      logger.warn("logout storage cleanup skipped", storageErr);
    }
    setUser(null);
    window.location.href = "/";
  }, []);

  // Memoize the context value so consumers don't re-render on every parent
  // render. Only changes when one of the fields actually changes.
  const value = useMemo(
    () => ({ user, setUser, loading, checkAuth, logout }),
    [user, loading, checkAuth, logout],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
